import { useEffect, useMemo, useState } from "react";
import api, { formatUSD, universeLabel, resolveImage } from "@/lib/api";
import { Plus, Trash2, Edit3, Search, Eye, EyeOff, ChevronUp, ChevronDown, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ImageUpload from "@/components/ImageUpload";
import MultiImageUpload from "@/components/MultiImageUpload";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [reordering, setReordering] = useState(false);

  function empty() {
    return {
      name: "",
      universe: "street_food",
      universes: [],
      category: "",
      price: 0,
      description: "",
      short_description: "",
      image: "",
      gallery: [],
      stock: 50,
      badge: "",
      featured: false,
      available: true,
      hidden: false,
      sort_order: 0,
    };
  }

  const load = () =>
    api.get("/admin/products").then((r) => {
      const list = [...r.data].sort((a, b) => {
        const ao = a.sort_order ?? 0;
        const bo = b.sort_order ?? 0;
        if (ao !== bo) return ao - bo;
        return (a.name || "").localeCompare(b.name || "");
      });
      setProducts(list);
    });
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase())),
    [products, search]
  );

  const onSave = async () => {
    try {
      const body = {
        name: form.name,
        price: parseFloat(form.price),
        stock: parseInt(form.stock),
        description: form.description,
        short_description: form.short_description || "",
        category: form.category,
        image: form.image || (form.gallery?.[0] || ""),
        gallery: form.gallery || [],
        universe: form.universe,
        universes: form.universes || [],
        badge: form.badge || null,
        featured: !!form.featured,
        available: form.available !== false,
        hidden: !!form.hidden,
        sort_order: parseInt(form.sort_order) || 0,
      };
      if (editing) {
        await api.patch(`/admin/products/${editing.id}`, body);
        toast.success("Produit mis à jour");
      } else {
        await api.post("/admin/products", body);
        toast.success("Produit créé");
      }
      setOpen(false);
      setEditing(null);
      setForm(empty());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Supprimer ce produit ?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Produit supprimé");
    load();
  };

  const onEdit = (p) => {
    setEditing(p);
    setForm({
      ...empty(),
      ...p,
      gallery: Array.isArray(p.gallery) ? p.gallery : [],
      available: p.available !== false,
      hidden: !!p.hidden,
      sort_order: p.sort_order ?? 0,
    });
    setOpen(true);
  };

  const togglePatch = async (p, patch, msgOk) => {
    try {
      await api.patch(`/admin/products/${p.id}`, patch);
      toast.success(msgOk);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const moveUp = async (idx) => {
    if (idx <= 0 || reordering) return;
    setReordering(true);
    const next = [...products];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setProducts(next);
    try {
      await api.post("/admin/products/reorder", { ordered_ids: next.map((p) => p.id) });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reorder failed");
      load();
    } finally {
      setReordering(false);
    }
  };
  const moveDown = async (idx) => {
    if (idx >= products.length - 1 || reordering) return;
    setReordering(true);
    const next = [...products];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    setProducts(next);
    try {
      await api.post("/admin/products/reorder", { ordered_ids: next.map((p) => p.id) });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reorder failed");
      load();
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-bold text-2xl md:text-3xl text-[var(--dd-green)]">Products</h1>
          <p className="text-[var(--dsf-text-muted)] text-sm mt-1">
            {products.length} products · drag-free reorder · per-product availability
          </p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty()); }}}>
          <DialogTrigger asChild>
            <button className="btn-mustard !py-2.5" data-testid="add-product-button"><Plus size={14} /> New product</button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-bold text-2xl text-[var(--dd-green)]">{editing ? "Edit" : "New"} product</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              <Field label="Name" value={form.name} onChange={(v) => setForm({...form, name: v})} testid="product-form-name" />
              <Field label="Category" value={form.category} onChange={(v) => setForm({...form, category: v})} testid="product-form-category" />
              <div>
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dd-green)]">Universe principal</Label>
                <select value={form.universe} onChange={(e) => setForm({...form, universe: e.target.value})}
                  className="w-full border border-[var(--dsf-border)] rounded-md px-3 py-2 mt-1 text-sm" data-testid="product-form-universe">
                  <option value="street_food">Dakar Street Food (Restaurant)</option>
                  <option value="street_market">Lunch Specials</option>
                  <option value="food_truck">Dakar Food Truck</option>
                </select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dd-green)]">Sort order (lower first)</Label>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  className="mt-1"
                  data-testid="product-form-sort-order"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dd-green)] block mb-2">Aussi visible dans (optionnel — services secondaires)</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="product-form-universes">
                  {[
                    { v: "street_food", label: "Restaurant" },
                    { v: "street_market", label: "Lunch Specials" },
                    { v: "food_truck", label: "Food Truck" },
                  ].filter((u) => u.v !== form.universe).map((u) => {
                    const checked = (form.universes || []).includes(u.v);
                    return (
                      <label key={u.v} className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition ${checked ? "border-[var(--dd-green)] bg-[var(--dd-green-soft)]" : "border-black/5 hover:border-[var(--dd-green)]"}`} data-testid={`product-universes-${u.v}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const set = new Set(form.universes || []);
                            if (e.target.checked) set.add(u.v); else set.delete(u.v);
                            setForm({ ...form, universes: Array.from(set) });
                          }}
                          className="accent-[var(--dd-green)]"
                        />
                        <span className="text-sm font-medium text-[var(--dd-green)]">{u.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <Field label="Price (USD)" type="number" value={form.price} onChange={(v) => setForm({...form, price: v})} testid="product-form-price" />
              <Field label="Stock" type="number" value={form.stock} onChange={(v) => setForm({...form, stock: v})} testid="product-form-stock" />

              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dd-green)] block mb-2">Cover photo</Label>
                <ImageUpload value={form.image} onChange={(v) => setForm({...form, image: v})} testid="product-form-image" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dd-green)] block mb-2">Gallery (additional photos)</Label>
                <MultiImageUpload
                  value={form.gallery}
                  onChange={(v) => setForm({ ...form, gallery: v })}
                  max={8}
                  testid="product-form-gallery"
                />
              </div>

              <div>
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dd-green)]">Badge</Label>
                <select value={form.badge || ""} onChange={(e) => setForm({...form, badge: e.target.value})}
                  className="w-full border border-[var(--dsf-border)] rounded-md px-3 py-2 mt-1 text-sm">
                  <option value="">None</option>
                  <option value="NEW">New</option>
                  <option value="BESTSELLER">Bestseller</option>
                  <option value="PROMO">Promo</option>
                  <option value="PREMIUM">Premium</option>
                </select>
              </div>
              <div className="grid grid-cols-1 gap-2 mt-6">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})} className="accent-[var(--dd-mustard)]" data-testid="product-form-featured" />
                  <span className="text-sm">Featured on home</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.available !== false} onChange={(e) => setForm({...form, available: e.target.checked})} className="accent-[var(--dd-green)]" data-testid="product-form-available" />
                  <span className="text-sm">Available for ordering</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!form.hidden} onChange={(e) => setForm({...form, hidden: e.target.checked})} className="accent-[var(--dd-coral)]" data-testid="product-form-hidden" />
                  <span className="text-sm">Hide from public site</span>
                </label>
              </div>

              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dd-green)]">Description</Label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  rows={3}
                  className="w-full border border-[var(--dsf-border)] rounded-md px-3 py-2 mt-1 text-sm"
                  data-testid="product-form-description"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dd-green)]">Short description</Label>
                <input
                  value={form.short_description || ""}
                  onChange={(e) => setForm({...form, short_description: e.target.value})}
                  className="w-full border border-[var(--dsf-border)] rounded-md px-3 py-2 mt-1 text-sm"
                  data-testid="product-form-short-description"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 sticky bottom-0 bg-white pt-3 border-t border-[var(--dsf-border)]">
              <button onClick={() => setOpen(false)} className="btn-pill-outline !py-2 !px-5">Cancel</button>
              <button onClick={onSave} className="btn-mustard !py-2" data-testid="product-form-submit">Save</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[var(--dsf-border)] rounded-2xl p-4 md:p-6">
        <div className="flex items-center gap-3 mb-5 border-b border-[var(--dsf-border)] pb-4">
          <Search size={16} className="text-[var(--dsf-text-muted)]" />
          <input
            placeholder="Search a product…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 outline-none text-sm"
            data-testid="products-search"
          />
        </div>

        {/* MOBILE: Card list */}
        <div className="md:hidden space-y-3">
          {filtered.map((p) => {
            const idx = products.findIndex((x) => x.id === p.id);
            const isAvail = p.available !== false;
            const isHidden = !!p.hidden;
            return (
              <div key={p.id} className="border border-[var(--dsf-border)] rounded-xl p-3" data-testid={`product-row-${p.id}`}>
                <div className="flex gap-3">
                  <img src={resolveImage(p.image)} alt="" className={`w-16 h-16 object-cover rounded-lg shrink-0 bg-[var(--dd-bone)] ${(!isAvail || isHidden) ? "grayscale opacity-70" : ""}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[var(--dsf-text)] truncate flex items-center gap-1.5">
                      {p.name}
                      {!isAvail && <span className="text-[8px] uppercase bg-[var(--dd-coral)] text-white px-1.5 py-0.5 rounded font-bold">SOLD OUT</span>}
                      {isHidden && <span className="text-[8px] uppercase bg-black/70 text-white px-1.5 py-0.5 rounded font-bold">HIDDEN</span>}
                    </div>
                    <div className="text-xs text-[var(--dsf-text-muted)] capitalize">{universeLabel(p.universe)}</div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="font-bold text-[var(--dd-green)] text-sm">{formatUSD(p.price)}</div>
                      <div className={`text-xs ${p.stock < 20 ? "text-[var(--dd-mustard-dark)] font-semibold" : "text-[var(--dsf-text-muted)]"}`}>Stock: {p.stock}</div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-3 pt-3 border-t border-black/5 flex-wrap">
                  <button onClick={() => moveUp(idx)} disabled={idx === 0 || reordering} className="px-2 py-1 rounded-md border border-[var(--dsf-border)] disabled:opacity-30" data-testid={`mobile-up-${p.id}`}><ChevronUp size={13} /></button>
                  <button onClick={() => moveDown(idx)} disabled={idx === products.length - 1 || reordering} className="px-2 py-1 rounded-md border border-[var(--dsf-border)] disabled:opacity-30" data-testid={`mobile-down-${p.id}`}><ChevronDown size={13} /></button>
                  <button
                    onClick={() => togglePatch(p, { available: !isAvail }, isAvail ? "Marked Sold Out" : "Marked Available")}
                    className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border ${isAvail ? "border-[var(--dd-green)] text-[var(--dd-green)]" : "border-[var(--dd-coral)] text-[var(--dd-coral)]"}`}
                    data-testid={`toggle-available-${p.id}`}
                  >
                    {isAvail ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {isAvail ? "Available" : "Sold out"}
                  </button>
                  <button
                    onClick={() => togglePatch(p, { hidden: !isHidden }, isHidden ? "Now visible" : "Now hidden")}
                    className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border ${isHidden ? "border-black/70 text-black/70" : "border-[var(--dsf-border)] text-[var(--dsf-text-muted)]"}`}
                    data-testid={`toggle-hidden-${p.id}`}
                  >
                    {isHidden ? <EyeOff size={11} /> : <Eye size={11} />} {isHidden ? "Hidden" : "Visible"}
                  </button>
                  <div className="ml-auto flex gap-1">
                    <button onClick={() => onEdit(p)} className="w-8 h-8 rounded-full border border-[var(--dsf-border)] flex items-center justify-center text-[var(--dd-green)] hover:bg-[var(--dd-bone)]" data-testid={`edit-product-${p.id}`}><Edit3 size={13} /></button>
                    <button onClick={() => onDelete(p.id)} className="w-8 h-8 rounded-full border border-[var(--dsf-border)] flex items-center justify-center text-[var(--dd-coral)] hover:bg-[var(--dd-bone)]" data-testid={`delete-product-${p.id}`}><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <div className="text-center text-[var(--dsf-text-muted)] py-8">No products</div>}
        </div>

        {/* DESKTOP: Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.18em] text-[var(--dsf-text-muted)] border-b border-[var(--dsf-border)]">
              <tr>
                <th className="text-left py-3 w-16">Order</th>
                <th className="text-left">Product</th>
                <th className="text-left">Universe</th>
                <th className="text-right">Price</th>
                <th className="text-right">Stock</th>
                <th className="text-center">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const idx = products.findIndex((x) => x.id === p.id);
                const isAvail = p.available !== false;
                const isHidden = !!p.hidden;
                return (
                  <tr key={p.id} className="border-b border-[var(--dsf-border)] hover:bg-[var(--dd-bone)]/40" data-testid={`product-row-desktop-${p.id}`}>
                    <td className="py-4">
                      <div className="flex flex-col items-center gap-0.5">
                        <button onClick={() => moveUp(idx)} disabled={idx === 0 || reordering} className="p-0.5 rounded hover:bg-[var(--dd-bone)] disabled:opacity-25" data-testid={`row-up-${p.id}`}>
                          <ChevronUp size={14} />
                        </button>
                        <span className="text-[10px] text-[var(--dsf-text-muted)] tabular-nums">{p.sort_order ?? 0}</span>
                        <button onClick={() => moveDown(idx)} disabled={idx === products.length - 1 || reordering} className="p-0.5 rounded hover:bg-[var(--dd-bone)] disabled:opacity-25" data-testid={`row-down-${p.id}`}>
                          <ChevronDown size={14} />
                        </button>
                      </div>
                    </td>
                    <td className="py-4">
                      <div className="flex items-center gap-3">
                        <img src={resolveImage(p.image)} alt="" className={`w-12 h-12 object-cover rounded-lg bg-[var(--dd-bone)] ${(!isAvail || isHidden) ? "grayscale opacity-60" : ""}`} />
                        <div>
                          <div className="font-medium flex items-center gap-1.5">
                            {p.name}
                            {(p.gallery?.length || 0) > 0 && (
                              <span className="text-[9px] bg-[var(--dd-green-soft)] text-[var(--dd-green)] px-1.5 py-0.5 rounded font-mono">+{p.gallery.length}📷</span>
                            )}
                          </div>
                          <div className="text-xs text-[var(--dsf-text-muted)]">{p.category}</div>
                        </div>
                      </div>
                    </td>
                    <td className="text-xs uppercase tracking-wider text-[var(--dsf-text-secondary)]">
                      {universeLabel(p.universe)}
                      {(p.universes || []).filter((u) => u !== p.universe).map((u) => (
                        <span key={u} className="ml-1.5 text-[9px] bg-[var(--dd-mustard)]/20 text-[var(--dd-mustard-dark)] px-1.5 py-0.5 rounded normal-case">+{universeLabel(u)}</span>
                      ))}
                    </td>
                    <td className="text-right font-medium">{formatUSD(p.price)}</td>
                    <td className={`text-right font-medium ${p.stock < 20 ? "text-[var(--dd-mustard-dark)]" : ""}`}>{p.stock}</td>
                    <td className="text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => togglePatch(p, { available: !isAvail }, isAvail ? "Marked Sold Out" : "Marked Available")}
                          className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium border ${isAvail ? "border-[var(--dd-green)] text-[var(--dd-green)]" : "border-[var(--dd-coral)] text-[var(--dd-coral)] bg-[var(--dd-coral)]/5"}`}
                          data-testid={`toggle-available-${p.id}`}
                          title="Toggle availability"
                        >
                          {isAvail ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {isAvail ? "Avail." : "Sold"}
                        </button>
                        <button
                          onClick={() => togglePatch(p, { hidden: !isHidden }, isHidden ? "Now visible" : "Now hidden")}
                          className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium border ${isHidden ? "border-black/70 text-black/70 bg-black/5" : "border-[var(--dsf-border)] text-[var(--dsf-text-muted)]"}`}
                          data-testid={`toggle-hidden-${p.id}`}
                          title="Toggle visibility"
                        >
                          {isHidden ? <EyeOff size={11} /> : <Eye size={11} />}
                        </button>
                      </div>
                    </td>
                    <td className="text-right">
                      <button onClick={() => onEdit(p)} className="p-2 hover:text-[var(--dd-mustard)]" data-testid={`edit-product-desktop-${p.id}`}><Edit3 size={14} /></button>
                      <button onClick={() => onDelete(p.id)} className="p-2 hover:text-[var(--dd-coral)]" data-testid={`delete-product-desktop-${p.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">{label}</Label>
    <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testid} />
  </div>
);
